/**
 * Support Tickets Page - List of user's support tickets
 */

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Display, Heading, Text } from '@/components/ui/typography';
import { CreateTicketForm, TicketCard, useTickets, type TicketStatus } from '@/features/support';
import { cn } from '@/lib/utils';
import { semanticTokens } from '@packages/config/theme';
import { Page } from '@supabank/ui';
import { SFIcon } from '@/components/ui/sf-icon';
import { sfIcons } from '@/lib/sf-icons';
import { InlineLoadingIndicator } from '@/components/ui/inline-loading';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const STATUS_TABS: { label: string; value: TicketStatus | undefined }[] = [
  { label: 'All', value: undefined },
  { label: 'Open', value: 'open' },
  { label: 'In Progress', value: 'in_progress' },
  { label: 'Resolved', value: 'resolved' },
];

export default function SupportTickets() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<TicketStatus | undefined>(undefined);
  const [showCreateForm, setShowCreateForm] = useState(false);

  const { tickets, total, isLoading, error, hasMore, refresh, loadMore } = useTickets({
    status: statusFilter,
  });

  const handleTicketCreated = (ticketId: string) => {
    setShowCreateForm(false);
    refresh();
    navigate(`/support/tickets/${ticketId}`);
  };

  return (
    <Page data-ux-contract="canon" className="bg-background min-h-screen">
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Link
              to="/support"
              className={cn(
                'p-2 -ml-2 rounded-lg',
                'hover:bg-muted/50',
                semanticTokens.motion.fast
              )}
            >
              <SFIcon icon={sfIcons.back} className="h-5 w-5" />
            </Link>
            <div>
              <Display variant="sm">My Tickets</Display>
              <Text variant="sm" color="muted">
                {total} ticket{total !== 1 ? 's' : ''}
              </Text>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={refresh}
              disabled={isLoading}
            >
              <SFIcon icon={sfIcons.refresh} className={cn('h-4 w-4', isLoading && 'animate-spin')} />
            </Button>
            <Button className="gap-2" onClick={() => setShowCreateForm(true)}>
              <SFIcon icon={sfIcons.add} className="h-4 w-4" />
              New Ticket
            </Button>
          </div>
        </div>

        {/* Create Ticket Form */}
        {showCreateForm && (
          <div className="mb-6">
            <CreateTicketForm
              onSuccess={handleTicketCreated}
              onCancel={() => setShowCreateForm(false)}
            />
          </div>
        )}

        {/* Status Tabs */}
        <div className="flex gap-1 p-1 bg-muted/50 rounded-lg mb-6 overflow-x-auto">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.label}
              onClick={() => setStatusFilter(tab.value)}
              className={cn(
                'px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap',
                'transition-colors duration-120',
                statusFilter === tab.value
                  ? 'bg-background shadow-sm text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Loading State */}
        {isLoading && tickets.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12">
            <InlineLoadingIndicator size="lg" className="mb-4" />
            <Text color="muted">Loading tickets...</Text>
          </div>
        )}

        {/* Error State */}
        {error && (
          <Card className="p-6 text-center border-destructive/50">
            <Text className="text-destructive mb-4">{error}</Text>
            <Button variant="outline" onClick={refresh}>
              Try Again
            </Button>
          </Card>
        )}

        {/* Empty State */}
        {!isLoading && !error && tickets.length === 0 && (
          <Card className="p-12 text-center">
            <div className={cn(
              'w-16 h-16 mx-auto mb-4 rounded-full',
              'bg-muted flex items-center justify-center'
            )}>
              <SFIcon icon={sfIcons.help} className="h-8 w-8 text-muted-foreground" />
            </div>
            <Heading variant="h3" className="mb-2">No tickets yet</Heading>
            <Text color="muted" className="mb-6">
              {statusFilter
                ? `You don't have any ${statusFilter.replace('_', ' ')} tickets.`
                : 'Need help? Start a conversation with our AI assistant or create a new ticket.'}
            </Text>
            <Button className="gap-2" onClick={() => setShowCreateForm(true)}>
              <SFIcon icon={sfIcons.add} className="h-4 w-4" />
              Create Ticket
            </Button>
          </Card>
        )}

        {/* Ticket List */}
        {tickets.length > 0 && (
          <div className="space-y-3">
            {tickets.map((ticket) => (
              <TicketCard
                key={ticket.id}
                ticket={ticket}
                onClick={() => navigate(`/support/tickets/${ticket.id}`)}
              />
            ))}

            {/* Load More */}
            {hasMore && (
              <div className="pt-4 text-center">
                <Button
                  variant="outline"
                  onClick={loadMore}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <InlineLoadingIndicator size="sm" className="mr-2" />
                      Loading...
                    </>
                  ) : (
                    'Load More'
                  )}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </Page>
  );
}
